import React from "react";
import PropTypes from "prop-types";
import { IoWarningOutline } from "react-icons/io5";
import { HiChatAlt2 } from "react-icons/hi";
import TimeCounter from "components/TimeCounter/TimeCounter";
import "./ChatCard.scss";
import { connect } from "react-redux";
import { setActiveConversation } from "actions/conversationActions";
import ChatExpirationService from "services/ChatExpirationService";
import {
  ChatExpirationEvents,
  MessageStatus,
} from "constants/ChatStatusConstants";
import { CountdownCircleTimer } from "react-countdown-circle-timer";
import colors from "styles/colors.module.scss";
import Button from "Button/Button";
import ExternalDisposeService from "services/ExternalDisposeService";
import { ConversationStatus } from "constants/ChatListingConstants";
import dayjs from "utils/dayjs";
import { changeAgentView } from "actions/agentViewActions";
import { AgentViewTypes } from "constants/ChatViewConstants";
import { getGroup } from "utils/Common";
import { UserGroup, UserBU } from "constants/UserConstants";
import { MessageSources } from "constants/MessageConstants";
import Channels from "components/Channels/Channels";

class ChatCard extends React.Component {
  constructor(props) {
    super(props);
    this.timerOffset = 3;
    this.agentGroup = getGroup();
    this.state = {
      showInActiveTimer: false,
      isChatExpired: false,
      initialTimeDuration: this.timerOffset * 1000,
      showNewMessage: false,
    };
  }

  componentDidMount() {
    const { chatDetails } = this.props;
    if (chatDetails.conversation.status === "OPEN") {
      const msgCount = chatDetails.conversation.messages.length;
      const lastMessage = chatDetails.conversation.messages[msgCount - 1];
      if (
        lastMessage.source === MessageSources.AGENT &&
        this.agentGroup !== UserGroup.SERVICE
      ) {
        this.setState({
          initialTimeDuration: this.getTimeDifference(lastMessage.messageTime),
          showInActiveTimer: true,
        });
      }
      if (
        lastMessage.source === MessageSources.BOT &&
        lastMessage.messageStatus === MessageStatus.AUTO_CLOSURE_WARNING &&
        this.agentGroup !== UserGroup.SERVICE
      ) {
        const secondLastMessage =
          chatDetails.conversation.messages[msgCount - 2];
        this.setState({
          initialTimeDuration: this.getTimeDifference(
            secondLastMessage.messageTime
          ),
          showInActiveTimer: true,
        });
      }

      //setting the timer for transferred case on page refresh
      if (
        lastMessage.source === MessageSources.SYSTEM &&
        lastMessage.messageStatus === MessageStatus.TRANSFERRED &&
        this.agentGroup !== UserGroup.SERVICE
      ) {
        if (msgCount >= 2) {
          const allMessages = chatDetails.conversation.messages;
          const filteredMsgs = allMessages.filter(
            (msg) =>
              msg.source !== MessageSources.SYSTEM &&
              msg.source !== MessageSources.BOT
          );
          const lastMsg = filteredMsgs[filteredMsgs.length - 1];
          if (lastMsg.source === MessageSources.AGENT) {
            this.setState({
              initialTimeDuration: this.getTimeDifference(lastMsg.messageTime),
              showInActiveTimer: true,
            });
          }
        }
      }
    }

    this.chatExpirationSubscription = ChatExpirationService.listen().subscribe(
      this.onMessageFromChatExpiration
    );
    if (
      this.props.chatDetails.conversation.status ===
      ConversationStatus.AUTO_CLOSURE
    ) {
      this.setState({
        isChatExpired: true,
      });
    }
  }

  getTimeDifference = (lastMessageTime) => {
    const currentDateTime = dayjs().utc().format("YYYY-MM-DD HH:mm:ss");
    const lastAgentMessageTime = dayjs(lastMessageTime)
      .utc()
      .format("YYYY-MM-DD HH:mm:ss");
    const diff = dayjs(currentDateTime).diff(dayjs(lastAgentMessageTime));

    if (isNaN(diff)) {
      return 0;
    }
    return parseInt(diff);
  };

  componentWillUnmount() {
    if (this.chatExpirationSubscription) {
      this.chatExpirationSubscription.unsubscribe();
    }
  }

  componentDidUpdate(prevProps) {
    const { chatDetails, isClosed, activeConversationId } = this.props;
    const isActiveConversation =
      chatDetails.conversationId === activeConversationId;
    const hasNewMessages =
      prevProps.chatDetails.conversation.messages.length <
      chatDetails.conversation.messages.length;
    if (!isClosed && !isActiveConversation && hasNewMessages) {
      this.setState({
        showNewMessage: true,
      });
    }
  }

  render() {
    const {
      className,
      chatDetails,
      isClosed,
      activeConversationId,
      agentView,
      isTransferredFrom,
      isTransferredTo,
    } = this.props;
    const { isChatExpired, showNewMessage } = this.state;
    const isActiveCard = chatDetails.conversationId === activeConversationId;
    const isOpenConversation =
      chatDetails.conversation.status === ConversationStatus.OPEN;
    const isActiveCls = isActiveCard ? "active" : "";
    const chatExpiredCls = isChatExpired ? "expired" : "";
    const isActiveAndOpen = isActiveCard && !isClosed;
    const isChatTransferring =
      agentView === AgentViewTypes.TRANSFER && isActiveAndOpen;
    const transfferingCls = isChatTransferring ? "transferring" : "";
    const isTransfferedCls = isTransferredFrom ? "transferred-from" : "";
    const isTransferredToCls =
      isTransferredTo &&
      isOpenConversation &&
      !isChatTransferring &&
      !isChatExpired
        ? "transferred-to"
        : "";
    return (
      <div
        className={`chat-card ${className} ${isActiveCls} ${chatExpiredCls} ${transfferingCls} ${isTransfferedCls} ${isTransferredToCls}`}
        onClick={this.onCardClick}
        data-testid="chat-card"
      >
        <div className="color-bg" />
        <div className="card-content">
          <div className="top-section">
            <Channels
              channels={[chatDetails.conversation.conversationMeta.channel]}
            />
            <div className="name-or-mobile">
              {chatDetails.name ? chatDetails.name : chatDetails.customerId}
            </div>
            {this.renderTimer()}
          </div>
          {isClosed && !isTransferredFrom && (
            <div className="disposition">{chatDetails.disposition}</div>
          )}
          {isClosed && !isTransferredFrom && (
            <div className="sub-disposition">{chatDetails.subDisposition}</div>
          )}
          {!isClosed && !isTransferredFrom && (
            <div className="last-msg" title={chatDetails.lastMsg}>
              {chatDetails.lastMsg}
            </div>
          )}
          {isTransferredFrom && (
            <div className="transfer-detail">{`Transferred to ${chatDetails.conversation.agentName}`}</div>
          )}
          <div className="city-duration-ctr">
            <div className="city">{chatDetails.city}</div>
            {isClosed && !isTransferredFrom && (
              <div className="duration">Duration: {chatDetails.duration}</div>
            )}
            {!isClosed && (
              <div className="buttons-container">
                {(isActiveAndOpen || isChatExpired) && (
                  <Button
                    className="dispose-btn"
                    label={"Dispose"}
                    onClick={this.onDisposeChat}
                  />
                )}
                {isActiveAndOpen && !isChatTransferring && !isChatExpired && (
                  <Button
                    className="transfer-btn"
                    label="Transfer"
                    onClick={this.onTransferChat}
                    isYellowButton
                  />
                )}
              </div>
            )}
          </div>
          {showNewMessage && (
            <div className="new-message">
              <HiChatAlt2 className="new-msg-icon" color={colors.orange} />
              <div className="message-text">New message</div>
            </div>
          )}
        </div>
      </div>
    );
  }

  onTransferChat = () => {
    this.props.changeAgentView(AgentViewTypes.TRANSFER);
  };

  onDisposeChat = (e) => {
    e.stopPropagation();
    const { chatDetails } = this.props;
    this.props.setActiveConversation({
      conversationId: chatDetails.conversationId,
      status: chatDetails.conversation.status,
    });
    ExternalDisposeService.onDispose(chatDetails.conversationId);
  };

  onMessageFromChatExpiration = (message) => {
    const { chatDetails } = this.props;
    if (
      !(message.conversation.conversationId === chatDetails.conversationId) ||
      this.agentGroup === UserGroup.SERVICE
    )
      return;
    switch (message.event) {
      case ChatExpirationEvents.START_WAITING:
        setTimeout(() => {
          this.setState({
            showInActiveTimer: true,
          });
        }, this.timerOffset * 1000);
        break;
      case ChatExpirationEvents.CANCEL_WAITING:
        if (this.state.showInActiveTimer) {
          this.setState({
            showInActiveTimer: false,
          });
        }
        break;
      case ChatExpirationEvents.FREEZE:
        this.setState({
          isChatExpired: true,
        });
        break;
      case ChatExpirationEvents.MSGS_LOADED:
        //this is when historical chat api has loaded all previous messages
        this.checkTimerForTransferredChat(message);
        break;
      default:
        return;
    }
  };

  checkTimerForTransferredChat = (eventMsg) => {
    const { chatDetails } = this.props;
    if (chatDetails.conversation.status === ConversationStatus.OPEN) {
      const msgCount = eventMsg.conversation.messages.length;
      const lastMessage = eventMsg.conversation.messages[msgCount - 1];
      if (
        lastMessage.source === MessageSources.SYSTEM &&
        lastMessage.messageStatus === MessageStatus.TRANSFERRED &&
        this.agentGroup !== UserBU.SERVICE
      ) {
        if (msgCount >= 2) {
          const allMessages = eventMsg.conversation.messages;
          const filteredMsgs = allMessages.filter(
            (msg) =>
              msg.source !== MessageSources.SYSTEM &&
              msg.source !== MessageSources.BOT
          );
          const lastMsg = filteredMsgs[filteredMsgs.length - 1];
          if (lastMsg.source === MessageSources.AGENT) {
            this.setState({
              initialTimeDuration: this.getTimeDifference(lastMsg.messageTime),
              showInActiveTimer: true,
            });
          }
        }
      }
    }
  };

  onCardClick = (e) => {
    e.stopPropagation();
    const { conversationId, conversation } = this.props.chatDetails;
    this.props.setActiveConversation({
      conversationId: conversationId,
      status: conversation.status,
    });
    this.setState({
      showNewMessage: false,
    });
  };

  renderTimer = () => {
    const { isClosed } = this.props;
    const {
      showInActiveTimer,
      initialTimeDuration,
      isChatExpired,
    } = this.state;
    return !isClosed && showInActiveTimer ? (
      <div className="inactive-timer">
        <div className="time-desc">
          <span className="inactive-text">{"Inactive since : "}</span>
          <TimeCounter
            key={`${initialTimeDuration}-timer`}
            className="chat-inactive-timer"
            initialTime={initialTimeDuration}
          />
          {!isChatExpired && (
            <CountdownCircleTimer
              key={`${initialTimeDuration}-circle-timer`}
              ariaLabel="circle-timer"
              size={20}
              strokeWidth={2}
              isPlaying
              duration={3 * 60 - this.timerOffset}
              colors={[
                [colors.supervisorStatGreen, 0.5],
                [colors.timerOrange, 0.5],
              ]}
            />
          )}
        </div>
        {isChatExpired && <IoWarningOutline className="timer-icon" />}
      </div>
    ) : (
      ""
    );
  };
}

ChatCard.propTypes = {
  className: PropTypes.string,
  setActiveConversation: PropTypes.func.isRequired,
  isClosed: PropTypes.bool,
  changeAgentView: PropTypes.func.isRequired,
  agentView: PropTypes.oneOf([AgentViewTypes.DEFAULT, AgentViewTypes.TRANSFER]),
  isTransferredFrom: PropTypes.bool,
  isTransferredTo: PropTypes.bool,
};

ChatCard.defaultProps = {
  className: "",
  isClosed: false,
  isTransferredFrom: false,
  isTransferredTo: false,
};

const mapStateToProps = (state) => ({
  activeConversationId: state.conversation.activeConversationId,
  agentView: state.agentView,
});

const mapDispatchToProps = {
  setActiveConversation,
  changeAgentView,
};

export default connect(mapStateToProps, mapDispatchToProps)(ChatCard);
